import React, { useState } from "react";
import {
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";

// Backend contract: POST /ecobalyse/upload-gtin-excel returns GtinUploadResult.
// 200 for SUCCESS/PARTIAL; 400/500 carry the same result in `details`.
const NOTICE_TYPES = { SUCCESS: "success", PARTIAL: "warning", FAILED: "error" };
const STATUS_COLORS = { SUCCESS: "green", PARTIAL: "#b26a00", FAILED: "red" };

const isGtinUploadResult = (value) =>
  !!value && typeof value.status === "string" && !!value.summary;

// Warnings are grouped by cause: { code, message, references, articleCount, rows, rowCount }.
// History written before the grouping stored one entry per article
// ({ articleId, reference, rows, code, message }); regroup those the same way.
export const normalizeEcobalyseWarnings = (warnings) => {
  if (!Array.isArray(warnings)) return [];
  const groups = new Map();
  for (const w of warnings) {
    if (!w || typeof w.message !== "string") continue;
    const key = JSON.stringify([w.code, w.message]);
    let group = groups.get(key);
    if (!group) {
      group = { code: w.code, message: w.message, references: new Set(), articles: new Set(), articleCount: 0, rows: new Set() };
      groups.set(key, group);
    }
    for (const reference of w.references || (w.reference != null ? [w.reference] : []))
      group.references.add(String(reference));
    if (w.articleId) group.articles.add(w.articleId);
    else group.articleCount += w.articleCount || 0;
    for (const row of w.rows || []) group.rows.add(row);
  }
  return [...groups.values()]
    .map(({ references, articles, articleCount, rows, ...rest }) => {
      const sortedRows = [...rows].sort((a, b) => a - b);
      return {
        ...rest,
        references: [...references],
        articleCount: articleCount + articles.size,
        rows: sortedRows,
        rowCount: sortedRows.length,
      };
    })
    .sort((a, b) => b.rowCount - a.rowCount || (a.rows[0] ?? 0) - (b.rows[0] ?? 0));
};

export const readGtinUploadResponse = async (response) => {
  const body = await response.json().catch(() => null);
  const result = response.ok ? body : body?.details;
  if (isGtinUploadResult(result))
    return { ...result, ecobalyseWarnings: normalizeEcobalyseWarnings(result.ecobalyseWarnings) };
  throw new Error(body?.message || "Error uploading file");
};

const parseJson = (value) => {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// Upload history keeps the GTIN result in `stacktrace` (details) and `warnings`.
// Older GTIN records use other formats and return null.
export const gtinResultFromHistory = (record) => {
  if (record?.tab !== "GTIN") return null;
  const details = parseJson(record.stacktrace);
  if (
    !details ||
    typeof details.code !== "string" ||
    !details.code.startsWith("GTIN_UPLOAD_") ||
    !Array.isArray(details.issues)
  )
    return null;
  const warnings = parseJson(record.warnings);
  return {
    uploadId: record.id,
    status: record.status,
    message: details.cause,
    summary: details.summary,
    assignmentErrors: details.issues.map((issue) => ({
      ...issue,
      rows: issue.rows || [],
      rowCount: issue.rowCount ?? issue.rows?.length ?? 0,
    })),
    ecobalyseWarnings: normalizeEcobalyseWarnings(warnings),
  };
};

export const gtinUploadNotice = (result) => ({
  message: result.message,
  type: NOTICE_TYPES[result.status] || "info",
});

// "3580–3604, 3612": ascending workbook rows collapsed into ranges.
export const formatRowRanges = (rows = []) => {
  const ranges = [];
  for (let i = 0; i < rows.length; i++) {
    const start = rows[i];
    while (i + 1 < rows.length && rows[i + 1] === rows[i] + 1) i++;
    ranges.push(start === rows[i] ? `${start}` : `${start}–${rows[i]}`);
  }
  return ranges.join(", ");
};

// One line per error group. Values are strings so Excel shows every digit.
export const gtinErrorRows = (result) => [
  ["Column", "Field", "Value", "Code", "Affected rows", "Row numbers", "Message"],
  ...(result.assignmentErrors || []).map((e) => [
    e.column,
    e.field,
    String(e.value ?? ""),
    e.code,
    e.rowCount,
    formatRowRanges(e.rows),
    e.message,
  ]),
];

// One line per cause. References are strings so Excel shows every digit.
export const gtinWarningRows = (result) => [
  ["Code", "Message", "Articles", "References", "Affected rows", "Row numbers"],
  ...(result.ecobalyseWarnings || []).map((w) => [
    w.code,
    w.message,
    w.articleCount,
    (w.references || []).join(", "),
    w.rowCount,
    formatRowRanges(w.rows),
  ]),
];

export const downloadGtinUploadDetails = async (result, fileName) => {
  const XLSX = await import("xlsx");
  const book = XLSX.utils.book_new();
  const errors = XLSX.utils.aoa_to_sheet(gtinErrorRows(result));
  errors["!cols"] = [
    { wch: 8 },
    { wch: 10 },
    { wch: 24 },
    { wch: 24 },
    { wch: 10 },
    { wch: 40 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(book, errors, "Errors");
  if (result.ecobalyseWarnings?.length) {
    const warnings = XLSX.utils.aoa_to_sheet(gtinWarningRows(result));
    warnings["!cols"] = [
      { wch: 24 },
      { wch: 70 },
      { wch: 9 },
      { wch: 40 },
      { wch: 10 },
      { wch: 40 },
    ];
    XLSX.utils.book_append_sheet(book, warnings, "Ecobalyse warnings");
  }
  const buffer = XLSX.write(book, { bookType: "xlsx", type: "array" });
  const url = URL.createObjectURL(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const detailsFileName = (uploadedName) =>
  `${(uploadedName || "gtins").replace(/\.[^.]+$/, "")}-upload-details.xlsx`;

// Groups are few in practice; the download keeps the complete list.
const MAX_ERROR_GROUPS_SHOWN = 50;

// "REFERENCE_NOT_FOUND" -> "REFERENCE NOT FOUND"; value and rows have their own columns.
export const errorLabel = (code) => String(code || "").replace(/_/g, " ");

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const ERROR_FILTERS = [
  { key: "ALL", label: "All" },
  { key: "Reference", label: "Reference" },
  { key: "GTIN", label: "GTIN" },
];

const GtinUploadErrors = ({ errors }) => {
  const [filter, setFilter] = useState("ALL");
  const count = (key) =>
    key === "ALL" ? errors.length : errors.filter((e) => e.field === key).length;
  const filtered =
    filter === "ALL" ? errors : errors.filter((e) => e.field === filter);
  const shown = filtered.slice(0, MAX_ERROR_GROUPS_SHOWN);
  return (
    <div style={{ marginTop: 12 }}>
      <div role="group" aria-label="Filter errors by field" style={{ marginBottom: 8 }}>
        {ERROR_FILTERS.map(({ key, label }) => (
          <Chip
            key={key}
            size="small"
            label={`${label} (${count(key)})`}
            clickable
            color={filter === key ? "primary" : "default"}
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            disabled={key !== "ALL" && count(key) === 0}
            style={{ marginRight: 8 }}
          />
        ))}
      </div>
      <div style={{ maxHeight: 400, overflow: "auto" }}>
      <Table size="small" aria-label="GTIN upload errors" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Value</TableCell>
            <TableCell>Error</TableCell>
            <TableCell align="right">Affected rows</TableCell>
            <TableCell>Row numbers</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((e) => (
            <TableRow key={`${e.code}-${e.column}-${e.value ?? ""}-${e.rows[0]}`}>
              <TableCell>
                {e.field}: {e.value || "(blank)"}
              </TableCell>
              <TableCell title={e.message}>{errorLabel(e.code)}</TableCell>
              <TableCell align="right">{e.rowCount}</TableCell>
              <TableCell>{formatRowRanges(e.rows)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      {filtered.length > shown.length && (
        <Typography variant="caption" color="textSecondary">
          Showing {shown.length} of {filtered.length} errors. Download the
          details for the complete list.
        </Typography>
      )}
    </div>
  );
};

// References after this many are left to the tooltip and the download.
const MAX_REFERENCES_SHOWN = 5;

export const referenceSummary = (references = []) =>
  references.length > MAX_REFERENCES_SHOWN
    ? `${references.slice(0, MAX_REFERENCES_SHOWN).join(", ")} +${references.length - MAX_REFERENCES_SHOWN} more`
    : references.join(", ");

const GtinUploadWarnings = ({ warnings }) => {
  const shown = warnings.slice(0, MAX_ERROR_GROUPS_SHOWN);
  return (
    <div style={{ marginTop: 16 }}>
      <Typography variant="subtitle2">Ecobalyse warnings</Typography>
      <Typography variant="caption" color="textSecondary">
        Warnings do not block GTIN assignments. Articles with an ineligibility cause stay N/A until it is fixed and the GTINs are uploaded again.
      </Typography>
      <div style={{ maxHeight: 400, overflow: "auto" }}>
        <Table size="small" aria-label="Ecobalyse warnings" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Cause</TableCell>
              <TableCell align="right">Articles</TableCell>
              <TableCell>References</TableCell>
              <TableCell align="right">Affected rows</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((w) => (
              <TableRow key={`${w.code}-${w.message}`}>
                <TableCell title={errorLabel(w.code)}>{w.message}</TableCell>
                <TableCell align="right">{w.articleCount}</TableCell>
                <TableCell title={(w.references || []).join(", ")}>
                  {referenceSummary(w.references)}
                </TableCell>
                <TableCell align="right" title={formatRowRanges(w.rows)}>
                  {w.rowCount}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {warnings.length > shown.length && (
        <Typography variant="caption" color="textSecondary">
          Showing {shown.length} of {warnings.length} causes. Download the
          details for the complete list.
        </Typography>
      )}
    </div>
  );
};

export const GtinUploadSummary = ({ result, uploadedName, showMessage = false }) => {
  const [downloading, setDownloading] = useState(false);
  if (!result) return null;
  // Summary can be missing on history records written before it was stored.
  const summary = result.summary;
  const errorCount = result.assignmentErrors?.length || 0;
  const referencesNotFound = summary?.referencesNotFound || 0;
  const warningCount = result.ecobalyseWarnings?.length || 0;

  const handleDownload = () => {
    setDownloading(true);
    downloadGtinUploadDetails(result, detailsFileName(uploadedName)).finally(
      () => setDownloading(false),
    );
  };

  return (
    <div role="status" aria-label="GTIN upload result" style={{ marginTop: 16 }}>
      <Typography
        variant="subtitle2"
        style={{ color: STATUS_COLORS[result.status] }}
      >
        {result.status}
      </Typography>
      {showMessage && result.message && (
        <Typography variant="body2">{result.message}</Typography>
      )}
      {summary && (
        <>
          <Typography variant="body2">
            Rows read: {summary.rowsRead} · processed: {summary.rowsProcessed} ·
            rejected: {summary.rowsRejected} · not processed:{" "}
            {summary.rowsNotProcessed}
            {referencesNotFound > 0 &&
              ` · distinct references not found: ${referencesNotFound}`}
          </Typography>
          <Typography variant="body2">
            GTIN associations created: {summary.associationsCreated} · already
            existing: {summary.associationsExisting}
            {warningCount > 0 && ` · Ecobalyse warnings: ${plural(warningCount, "cause")}`}
          </Typography>
        </>
      )}
      {(errorCount > 0 || warningCount > 0) && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleDownload}
          disabled={downloading}
          style={{ marginTop: 8 }}
        >
          {warningCount > 0
            ? `Download errors and Ecobalyse warnings (${plural(errorCount, "error")}, ${plural(warningCount, "warning")})`
            : `Download errors (${plural(errorCount, "error")})`}
        </Button>
      )}
      {errorCount > 0 && <GtinUploadErrors errors={result.assignmentErrors} />}
      {warningCount > 0 && <GtinUploadWarnings warnings={result.ecobalyseWarnings} />}
    </div>
  );
};

/** GTIN view for the upload history detail page; null for other uploads. */
export const GtinUploadHistoryResult = ({ record }) => {
  const result = gtinResultFromHistory(record);
  if (!result) return null;
  return (
    <div style={{ width: "100%" }}>
      <GtinUploadSummary
        result={result}
        uploadedName={record.filename}
        showMessage
      />
    </div>
  );
};
